import { useState } from 'react';
import { AlertTriangle, CheckCircle2, UserX, XCircle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const ACTIONS = {
  realizada: {
    label: 'Marcar como Realizada',
    description: 'Esta ação confirma que a consulta foi atendida normalmente.',
    detail: '',
    icon: CheckCircle2,
    color: 'text-green-600',
    bg: 'bg-green-100',
    btnClass: 'bg-green-600 hover:bg-green-700 text-white',
    cta: 'Consulta Realizada',
  },
  falta: {
    label: 'Marcar como Falta',
    description: 'Esta ação registra que a paciente não compareceu à consulta.',
    detail: 'Faltas impactam o controle de retorno e as estatísticas de no-show. Use apenas quando a paciente realmente não compareceu.',
    icon: UserX,
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    btnClass: 'bg-amber-500 hover:bg-amber-600 text-white',
    cta: 'Paciente Faltou',
  },
};

export default function ConfirmarStatusModal({ open, onOpenChange, action, consulta, onConfirm, loading = false }) {
  if (!action || !ACTIONS[action]) return null;
  const cfg = ACTIONS[action];
  const Icon = cfg.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={`modal-confirmar-${action}`}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-full ${cfg.bg} ${cfg.color} flex items-center justify-center flex-shrink-0`}>
              <Icon className="w-5 h-5" strokeWidth={2.2} />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-base font-bold text-gray-900">{cfg.label}</DialogTitle>
              <DialogDescription className="text-sm text-gray-600 mt-1">
                {cfg.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {consulta && (
          <div className="bg-gray-50 rounded-lg p-3.5 space-y-1.5 my-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Consulta</div>
            <div className="text-sm font-semibold text-gray-900">{consulta.paciente_nome || '—'}</div>
            <div className="text-xs text-gray-700">
              {consulta.categoria || consulta.tipo_consulta || 'Consulta'}
            </div>
            <div className="text-xs text-gray-500 font-mono tabular-nums">
              {consulta.data_consulta} · {consulta.hora_inicio}
              {typeof consulta.dias_atras === 'number' && consulta.dias_atras > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 uppercase tracking-wider">
                  há {consulta.dias_atras} {consulta.dias_atras === 1 ? 'dia' : 'dias'}
                </span>
              )}
            </div>
          </div>
        )}

        {cfg.detail && (
          <p className="text-xs text-gray-600 leading-relaxed">{cfg.detail}</p>
        )}

        <DialogFooter className="gap-2 sm:gap-2 mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            data-testid={`modal-${action}-cancel`}
          >
            Cancelar
          </Button>
          <Button
            className={cfg.btnClass}
            onClick={onConfirm}
            disabled={loading}
            data-testid={`modal-${action}-confirm`}
          >
            {loading ? 'Salvando…' : cfg.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
